import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { 
  HiOutlineCloudUpload,
  HiOutlineShieldCheck,
  HiOutlineDocumentDownload,
  HiOutlineTrash,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlineLockClosed,
  HiOutlineKey,
} from 'react-icons/hi';
import { 
  VscFile,
  VscFileCode,
  VscFilePdf,
  VscFileZip,
  VscFileMedia,
  VscFileSubmodule 
} from 'react-icons/vsc';
import { motion } from 'framer-motion';
import { 
  RiFileTextLine,
  RiFilePdfLine,
  RiFileZipLine,
  RiFileCodeLine,
  RiFileImageLine,
  RiFileWordLine,
  RiArrowRightLine,
  RiLockLine
} from 'react-icons/ri';
import { 
  LuFileText,
  LuShieldCheck,
  LuClock,
  LuTrash2,
  LuLock,
  LuKey,
  LuArrowRight,
  LuDownload,
  LuUpload,
} from 'react-icons/lu';

const IllustrationWrapper = ({ children }) => {
  const { darkMode } = useDarkMode();
  return (
    <div className="relative w-full h-full">
      {/* Update background gradient for dark mode */}
      <div className={`absolute inset-0 rounded-xl transition-colors duration-300 
        bg-gradient-to-b from-primary-50/30 to-transparent
        ${darkMode ? 'dark:from-gray-900 dark:to-gray-800/80' : ''}`}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        {children}
      </div>
    </div>
  );
};

const ProcessStep = ({ icon, title, description, illustration, index }) => {
  const { darkMode } = useDarkMode();
  return (
    <motion.div
      role="article"
      aria-label={`${title}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative group"
    >
      <div className={`space-y-6 rounded-2xl p-8 h-[420px] flex flex-col border shadow-sm transition-all duration-300
        ${darkMode
          ? 'bg-gray-900/90 backdrop-blur-md border-gray-800/80 hover:bg-gray-900/95 hover:shadow-lg'
          : 'bg-white/40 backdrop-blur-sm border-primary-100/20 hover:bg-white/70 hover:shadow-lg'}
      `}>
        {/* Illustration container with fixed height */}
        <div className="relative h-48 flex-shrink-0">
          <div className="relative h-full flex items-center justify-center">
            <div className={darkMode ? 'drop-shadow-[0_2px_16px_rgba(16,185,129,0.10)]' : ''}>
              {illustration}
            </div>
          </div>
        </div>
        {/* Content section with fixed height */}
        <div className="space-y-3 flex-1 flex flex-col">
          <div className="flex items-center gap-3 flex-shrink-0">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="group relative"
            >
              <motion.div
                whileHover={{ rotate: 5 }}
                className={`text-2xl ${stepIconColors[index]} transition-all duration-300
                  ${darkMode ? 'drop-shadow-[0_2px_8px_rgba(16,185,129,0.15)]' : ''}`}
              >
                {icon}
              </motion.div>
            </motion.div>
            <h3 className={`text-xl font-semibold tracking-tight ${darkMode ? 'text-neutral-100' : 'text-gray-800/90'}`}>{title}</h3>
          </div>
          <p className={`leading-relaxed flex-1 ${darkMode ? 'text-neutral-300' : 'text-gray-600/80'}`}>{description}</p>
        </div>
      </div>
      {/* Update connecting elements with primary colors */}
      {index < 5 && (
        <>
          {index % 3 !== 2 && (
            <div className="absolute -right-12 top-1/2 transform -translate-y-1/2 hidden lg:block w-24">
              <div className={`h-[1px] bg-gradient-to-r from-primary-200/50 via-primary-100/30 to-transparent
                ${darkMode ? 'dark:from-gray-700/60 dark:via-gray-800/40' : ''}`}
              />
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

const FileIcon = () => {
  const [currentIcon, setCurrentIcon] = useState(0);
  const icons = [
    { Icon: LuFileText, color: 'text-primary-500' },
    { Icon: LuFileText, color: 'text-accent-500' },
    { Icon: LuFileText, color: 'text-yellow-500' },
    { Icon: LuFileText, color: 'text-green-500' },
    { Icon: LuFileText, color: 'text-purple-500' }
  ];

  const { Icon, color } = icons[currentIcon];
  
  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      className={`text-6xl ${color} transition-colors duration-300`}
    >
      <Icon className="stroke-[1.5]" />
    </motion.div>
  );
};

const FileConversionIllustration = () => (
  <div className="relative w-full h-full flex items-center justify-center gap-8">
    <div className="relative">
      <FileIcon />
    </div>

    <div className="text-primary-400">
      <LuArrowRight className="text-2xl" />
    </div>

    <div className="relative">
      <motion.div
        className="text-6xl text-accent-500 relative"
        whileHover={{ scale: 1.05 }}
      >
        <LuFileText className="stroke-[1.5]" />
        <div className="absolute -top-2 -right-2 bg-accent-400/10 rounded-full p-1.5">
          <LuLock className="text-2xl text-accent-500 stroke-[1.5]" />
        </div>
      </motion.div>
    </div>
  </div>
);

// Enhanced SecurityIllustration with better visual hierarchy
const SecurityIllustration = () => (
  <IllustrationWrapper>
    <div className="relative">
      <div className="relative">
        <div className="absolute inset-0 bg-primary-400/20 rounded-full blur-xl transform scale-90" />
        <LuShieldCheck className="text-6xl text-primary-500 relative z-10 stroke-[1.5]" />
      </div>
      
      <motion.div
        className="absolute -top-4 -right-4 bg-accent-400/10 rounded-full p-2"
        whileHover={{ scale: 1.1 }}
      >
        <LuKey className="text-3xl text-accent-500 stroke-[1.5]" />
      </motion.div>
    </div>
  </IllustrationWrapper>
);

// Enhanced Download & Auto-Delete illustration
const DownloadIllustration = () => (
  <IllustrationWrapper>
    <div className="relative">
      <div className="relative">
        <div className="absolute inset-0 bg-primary-400/20 rounded-full blur-xl transform scale-90" />
        <LuDownload className="text-6xl text-primary-500 relative z-10 stroke-[1.5]" />
      </div>

      <motion.div
        className="absolute -top-4 -right-4 bg-accent-400/10 rounded-full p-2"
        whileHover={{ scale: 1.1 }}
      >
        <LuTrash2 className="text-3xl text-accent-500 stroke-[1.5]" />
      </motion.div>
    </div>
  </IllustrationWrapper>
);

// Manual Deletion Illustration
const ManualDeletionIllustration = () => (
  <IllustrationWrapper>
    <div className="relative">
      <div className="relative">
        <div className="absolute inset-0 bg-accent-400/20 rounded-full blur-xl transform scale-90" />
        <LuTrash2 className="text-6xl text-accent-500 relative z-10 stroke-[1.5]" />
      </div>
      
      <motion.div
        className="absolute -top-4 -right-4 bg-primary-400/10 rounded-full p-2"
        whileHover={{ scale: 1.1 }}
      >
        <LuShieldCheck className="text-3xl text-primary-500 stroke-[1.5]" />
      </motion.div>
    </div>
  </IllustrationWrapper>
);

// Time-Based Cleanup Illustration
const TimeCleanupIllustration = () => (
  <IllustrationWrapper>
    <div className="relative">
      <div className="relative">
        <div className="absolute inset-0 bg-primary-400/20 rounded-full blur-xl transform scale-90" />
        <LuClock className="text-6xl text-primary-500 relative z-10 stroke-[1.5]" />
      </div>
      
      <motion.div
        className="absolute -top-4 -right-4 bg-primary-400/10 rounded-full p-2"
        whileHover={{ scale: 1.1 }}
      >
        <LuTrash2 className="text-3xl text-primary-500 stroke-[1.5]" />
      </motion.div>
    </div>
  </IllustrationWrapper>
);

// Privacy Guaranteed Illustration
const PrivacyIllustration = () => (
  <IllustrationWrapper>
    <div className="relative">
      <div className="relative">
        <div className="absolute inset-0 bg-accent-400/20 rounded-full blur-xl transform scale-90" />
        <LuShieldCheck className="text-6xl text-accent-500 relative z-10 stroke-[1.5]" />
      </div>
      
      <motion.div
        className="absolute -top-4 -right-4 bg-accent-400/10 rounded-full p-2"
        whileHover={{ scale: 1.1 }}
      >
        <LuLock className="text-3xl text-accent-500 stroke-[1.5]" />
      </motion.div>
    </div>
  </IllustrationWrapper>
);

const stepIconColors = {
  0: "text-primary-500",
  1: "text-accent-500", 
  2: "text-primary-600",
  3: "text-accent-600",
  4: "text-primary-500",
  5: "text-accent-500"
};

const HowItWorks = () => {
  const { darkMode } = useDarkMode();
  const steps = [
    {
      icon: <LuUpload className="stroke-[1.5]" />,
      title: "Upload & Convert",
      description: (
        <>
          When you upload a file and click convert, your file is immediately{" "}
          <span className="text-primary-500 font-semibold">encrypted</span>{" "}
          before being sent to our servers.
        </>
      ),
      illustration: <FileConversionIllustration />
    },
    {
      icon: <LuShieldCheck className="stroke-[1.5]" />,
      title: "Secure Processing",
      description: (
        <>
          A special{" "}<span className="text-primary-500 font-semibold">token</span>{" "}
          is created and linked with your file and{" "}
          <span className="text-primary-500 font-semibold">user ID</span>. This trio is verified at every 
          step of the conversion process, ensuring only you have access to your files.
        </>
      ),
      illustration: <SecurityIllustration />
    },
    {
      icon: <LuDownload className="stroke-[1.5]" />,
      title: "Download & Auto-Delete",
      description: (
        <>
          Click the <span className="text-primary-500 font-semibold">download button</span> to get your converted file. 
          Once downloaded, your file is automatically and permanently <span className="text-primary-500 font-semibold">deleted</span>  from our servers.
        </>
      ),
      illustration: <DownloadIllustration />
    },
    {
      icon: <LuTrash2 className="stroke-[1.5]" />,
      title: "Manual Deletion",
      description: (
        <>
          If you choose to click the <span className="text-primary-500 font-semibold">delete button</span> instead 
          of converting, your file is immediately removed from our servers, no questions asked.
        </>
      ),
      illustration: <ManualDeletionIllustration />
    },
    {
      icon: <LuClock className="stroke-[1.5]" />,
      title: "Time-Based Cleanup",
      description: (
        <>
          For additional security, any files that haven't been downloaded within{" "}
          <span className="text-primary-500 font-semibold">one hour</span>{" "}
          are automatically deleted from our servers.
        </>
      ),
      illustration: <TimeCleanupIllustration />
    },
    {
      icon: <LuShieldCheck className="stroke-[1.5]" />,
      title: "Privacy Guaranteed",
      description: (
        <>
          Your <span className="text-primary-500 font-semibold">privacy</span> is our priority. Files are either 
          downloaded and deleted, manually deleted, or automatically cleaned up - ensuring your data never lingers 
          on our servers.
        </>
      ),
      illustration: <PrivacyIllustration />
    }
  ];

  return (
    <div className={`py-20 lg:py-28 transition-colors duration-300 
      bg-gradient-to-b from-neutral-50 to-white
      ${darkMode ? 'dark:from-gray-950 dark:to-gray-900' : ''}`}
    >
      <motion.div
        className="text-center mb-24 lg:mb-32 px-6"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <span className={`font-medium tracking-wider text-sm uppercase mb-4 block
          ${darkMode ? 'text-primary-400' : 'text-primary-500'}`}
        >
          Security First
        </span>
        <h2 className={`text-4xl md:text-5xl font-bold mb-8 tracking-tight leading-tight
          ${darkMode ? 'text-neutral-100 drop-shadow-[0_2px_16px_rgba(16,185,129,0.10)]' : 'text-neutral-800/90'}`}
        >
          Your File's Journey
        </h2>
        <div className={`w-24 h-0.5 mx-auto mb-8 
          ${darkMode ? 'bg-gradient-to-r from-primary-700/60 via-primary-800/30 to-transparent' : 'bg-gradient-to-r from-primary-500/40 via-primary-400/20 to-transparent'}`}
        />
        <p className={`text-lg max-w-2xl mx-auto leading-relaxed font-normal
          ${darkMode ? 'text-neutral-300' : 'text-neutral-600/90'}`}
        >
          Security and privacy at every step. Here's how we handle your files 
          during the conversion process.
        </p>
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 lg:gap-x-12 gap-y-12 lg:gap-y-16 relative">
          {/* More subtle background pattern */}
          <div className="absolute inset-0 hidden lg:block pointer-events-none">
            <div className={`h-full w-full opacity-[0.10] [background-size:24px_24px]
              ${darkMode ? 'bg-[radial-gradient(#334155_0.3px,transparent_0.3px)]' : 'bg-[radial-gradient(#3b82f6_0.2px,transparent_0.2px)]'}`}
            />
            <div className={`absolute inset-0 
              ${darkMode ? 'bg-gradient-to-b from-transparent via-gray-900/80 to-gray-900' : 'bg-gradient-to-b from-transparent via-white/50 to-white'}`}
            />
          </div>
          
          {steps.map((step, index) => (
            <ProcessStep
              key={index}
              index={index}
              icon={step.icon}
              title={step.title}
              description={step.description}
              illustration={step.illustration}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
